import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction, roundTo, toLatex, toText } from '@yuan-math/math';
import { choiceExercise, fractionExercise, fractionTrap, numericExercise, options, template, trap } from './authoring.ts';

/**
 * Probability, of one event and of two.
 *
 * Two wrong answers dominate the single-event templates, and both are worth
 * naming: giving the count of favourable outcomes instead of a fraction, and
 * comparing favourable to *unfavourable* — which is odds, a perfectly good
 * quantity answering a different question.
 *
 * The compound templates exist for a third: adding two probabilities when both
 * events must happen. It is worth provoking rather than avoiding, because the
 * answer it produces is larger than either part, and noticing *that* is the
 * skill — not remembering which operation the textbook said to use.
 */
export const PROBABILITY_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'probability-single-event',
    conceptId: 'basic-probability',
    description: 'Find the probability of one outcome, as a fraction.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const wanted = rng.int(1, difficulty === 2 ? 6 : 9);
      const other = rng.int(1, difficulty === 2 ? 6 : 9);
      const total = wanted + other;
      const scenario = rng.pick([
        { first: 'red', second: 'blue', noun: 'counters', container: 'bag' },
        { first: 'green', second: 'yellow', noun: 'marbles', container: 'jar' },
        { first: 'striped', second: 'plain', noun: 'socks', container: 'drawer' },
      ]);

      return fractionExercise({
        prompt: `A ${scenario.container} holds ${wanted} ${scenario.first} ${scenario.noun} and ${other} ${scenario.second} ones. One is taken out without looking. What is the probability it is ${scenario.first}? Give your answer as a fraction in its simplest form.`,
        value: fraction(wanted, total),
        requireSimplified: true,
        summary: `There are ${wanted} + ${other} = ${total} ${scenario.noun} altogether, and ${wanted} of them are ${scenario.first}, so the probability is ${toText(fraction(wanted, total))}.`,
        hint: 'A probability compares what you want with everything that could happen — so the total goes underneath.',
        steps: [
          { label: 'Count every possible outcome.', latex: `${wanted} + ${other} = ${total}` },
          { label: 'Put the favourable outcomes over the total.', latex: toLatex(fraction(wanted, total)) },
        ],
        traps: [
          /*
           * Odds rather than probability: wanted compared to unwanted. With a
           * single unwanted outcome this normalises to a bare `${wanted}` —
           * identical to the count trap below — so the two errors could not be
           * told apart. A plain whole number is far more often a count.
           */
          ...(other === 1 ? [] : [{ response: `${wanted}/${other}`, misconceptionId: 'probability-as-odds' }]),
          trap(wanted, 'probability-as-count'),
        ],
      });
    },
  }),

  template({
    id: 'probability-complement',
    conceptId: 'basic-probability',
    description: 'Find the probability that something does not happen.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const wanted = rng.int(1, 9);
      const other = rng.int(1, 9);
      const total = wanted + other;
      const scenario = rng.pick([
        { first: 'red', noun: 'counters', container: 'bag' },
        { first: 'green', noun: 'marbles', container: 'jar' },
      ]);

      return fractionExercise({
        prompt: `A ${scenario.container} holds ${total} ${scenario.noun}, of which ${wanted} are ${scenario.first}. One is taken at random. What is the probability that it is **not** ${scenario.first}? Give your answer as a fraction in its simplest form.`,
        value: fraction(other, total),
        requireSimplified: true,
        summary: `Everything that is not ${scenario.first} accounts for ${total} − ${wanted} = ${other} of the ${total}, so the probability is ${toText(fraction(other, total))}.`,
        hint: 'Either count the ones that are not red, or take the probability that it *is* red away from 1. Both work.',
        steps: [
          { label: 'Count the outcomes that are not favourable.', latex: `${total} - ${wanted} = ${other}` },
          { label: 'Put that over the total.', latex: toLatex(fraction(other, total)) },
          { label: 'Or subtract from 1.', latex: `1 - ${toLatex(fraction(wanted, total))} = ${toLatex(fraction(other, total))}` },
        ],
        traps: [
          // Same collision as above, the other way round.
          ...(wanted === 1 ? [] : [{ response: `${other}/${wanted}`, misconceptionId: 'probability-as-odds' }]),
          trap(other, 'probability-as-count'),
        ],
      });
    },
  }),

  template({
    id: 'probability-as-percent',
    conceptId: 'basic-probability',
    description: 'Express a probability as a percentage.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const total = rng.pick([4, 5, 8, 10, 20, 25, 50]);
      const wanted = rng.int(1, total - 1);
      const answer = roundTo((wanted / total) * 100, 2);

      return numericExercise({
        prompt: `A spinner has ${total} equal sections, and ${wanted} of them are shaded. What is the probability of landing on a shaded section, as a percentage?`,
        unit: '%',
        value: answer,
        summary: `${wanted} out of ${total} is ${toText(fraction(wanted, total))}, and as a percentage that is ${answer}%.`,
        hint: 'Write it as a fraction of the total first, then turn that fraction into hundredths.',
        steps: [
          { label: 'Write the probability as a fraction.', latex: toLatex(fraction(wanted, total)) },
          { label: 'Multiply by 100 to get a percentage.', latex: `${answer}\\%` },
        ],
        traps: [
          trap(wanted, 'probability-as-count'),
          trap(roundTo((wanted / (total - wanted)) * 100, 2), 'probability-as-odds'),
        ],
      });
    },
  }),

  template({
    id: 'probability-certainty-scale',
    conceptId: 'basic-probability',
    description: 'Recognise the probability of a certain or impossible event.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const total = rng.int(3, 12);
      const certain = rng.bool();
      const colour = rng.pick(['red', 'blue', 'green']);
      const answer = certain ? 1 : 0;

      return numericExercise({
        prompt: certain
          ? `A bag holds ${total} ${colour} counters and nothing else. What is the probability of taking out a ${colour} counter?`
          : `A bag holds ${total} ${colour} counters and nothing else. What is the probability of taking out a white counter?`,
        value: answer,
        summary: certain
          ? `Every one of the ${total} counters is ${colour}, so it is certain — and certainty is 1.`
          : `There are no white counters at all, so it cannot happen — and impossibility is 0.`,
        hint: 'Probability runs from 0 (cannot happen) to 1 (must happen). Where does this sit?',
        steps: [
          {
            label: certain ? 'Every outcome is favourable.' : 'No outcome is favourable.',
            latex: certain ? `\\frac{${total}}{${total}} = 1` : `\\frac{0}{${total}} = 0`,
          },
        ],
        traps: [trap(certain ? total : 100, 'probability-as-count')],
      });
    },
  }),

  /* --------------------------------------------------- compound events -- */
  template({
    id: 'compound-independent-both',
    conceptId: 'compound-probability',
    description: 'Find the probability that two independent events both happen.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const firstTotal = rng.pick(difficulty === 4 ? [4, 5, 6, 8, 10] : [2, 3, 4, 5]);
      const firstWanted = rng.int(1, firstTotal - 1);
      const secondTotal = rng.pick(difficulty === 4 ? [4, 5, 6, 8, 10] : [2, 3, 4, 6]);
      const secondWanted = rng.int(1, secondTotal - 1);
      const scenario = rng.pick([
        { firstEvent: 'gold', second: `a bag of ${secondTotal} counters, ${secondWanted} of them red`, secondEvent: 'red' },
        { firstEvent: 'striped', second: `a jar of ${secondTotal} marbles, ${secondWanted} of them green`, secondEvent: 'green' },
      ]);

      const answer = fraction(firstWanted * secondWanted, firstTotal * secondTotal);
      const added = fraction(firstWanted * secondTotal + secondWanted * firstTotal, firstTotal * secondTotal);

      return fractionExercise({
        prompt: `A spinner has ${firstTotal} equal sections, ${firstWanted} of them ${scenario.firstEvent}. Beside it is ${scenario.second}. The spinner is spun and one item is taken at random. What is the probability of ${scenario.firstEvent} **and** ${scenario.secondEvent}? Give your answer as a fraction in its simplest form.`,
        value: answer,
        requireSimplified: true,
        summary: `The two are independent, so multiply: ${toText(fraction(firstWanted, firstTotal))} × ${toText(fraction(secondWanted, secondTotal))} = ${toText(answer)}.`,
        hint: 'Both have to happen, so the answer is smaller than either one on its own. That rules out adding.',
        steps: [
          { label: 'Write each probability.', latex: `${toLatex(fraction(firstWanted, firstTotal))} \\quad ${toLatex(fraction(secondWanted, secondTotal))}` },
          { label: 'Multiply them.', latex: `${toLatex(fraction(firstWanted, firstTotal))} \\times ${toLatex(fraction(secondWanted, secondTotal))} = ${toLatex(answer)}` },
        ],
        traps: [
          // Adding the two probabilities. Dropped in the rare case where the
          // sum happens to land on the product.
          ...(added.n * answer.d === answer.n * added.d ? [] : [fractionTrap(added, 'compound-probability-added')]),
          // The count of favourable pairs, with no denominator at all.
          ...(firstWanted * secondWanted === 1 ? [] : [trap(firstWanted * secondWanted, 'probability-as-count')]),
        ],
      });
    },
  }),

  template({
    id: 'compound-sample-space-size',
    conceptId: 'compound-probability',
    description: 'Count the outcomes of a two- or three-stage experiment.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const stages = difficulty === 3 ? 3 : 2;
      const a = rng.pick([2, 3, 4, 5, 6]);
      const b = rng.pick([2, 3, 4, 5, 6, 8]);
      const c = stages === 3 ? rng.pick([2, 3, 4]) : 1;
      const answer = a * b * c;
      const sum = stages === 3 ? a + b + c : a + b;

      return numericExercise({
        prompt:
          stages === 3
            ? `A spinner with ${a} equal sections is spun, a die with ${b} faces is rolled, and one of ${c} differently coloured counters is drawn. How many outcomes are there altogether?`
            : `A spinner with ${a} equal sections is spun and a die with ${b} faces is rolled. How many outcomes are there altogether?`,
        unit: 'outcomes',
        value: answer,
        summary: `Each outcome of one stage pairs with every outcome of the next, so the count is ${stages === 3 ? `${a} × ${b} × ${c}` : `${a} × ${b}`} = ${answer}.`,
        hint: 'Picture a grid rather than a queue. Every row is a whole copy of the next stage.',
        steps: [
          { label: 'Count the outcomes of each stage.', latex: stages === 3 ? `${a},\\; ${b},\\; ${c}` : `${a},\\; ${b}` },
          { label: 'Multiply them together.', latex: `${stages === 3 ? `${a} \\times ${b} \\times ${c}` : `${a} \\times ${b}`} = ${answer}` },
        ],
        traps: [...(sum === answer ? [] : [trap(sum, 'sample-space-undercounted')])],
      });
    },
  }),

  template({
    id: 'compound-without-replacement',
    conceptId: 'compound-probability',
    description: 'Two draws with the first item kept out, so the second probability changes.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const wanted = rng.int(2, difficulty === 5 ? 7 : 5);
      const other = rng.int(2, difficulty === 5 ? 8 : 5);
      const total = wanted + other;
      const scenario = rng.pick([
        { colour: 'red', noun: 'counters', container: 'bag' },
        { colour: 'green', noun: 'marbles', container: 'jar' },
        { colour: 'blue', noun: 'beads', container: 'tin' },
      ]);

      const answer = fraction(wanted * (wanted - 1), total * (total - 1));
      const replaced = fraction(wanted * wanted, total * total);
      const added = fraction(wanted * (total - 1) + (wanted - 1) * total, total * (total - 1));

      return fractionExercise({
        prompt: `A ${scenario.container} holds ${wanted} ${scenario.colour} ${scenario.noun} and ${other} others. Two are taken out one after the other, and the first is **not** put back. What is the probability that both are ${scenario.colour}? Give your answer as a fraction in its simplest form.`,
        value: answer,
        requireSimplified: true,
        summary: `The first is ${scenario.colour} with probability ${toText(fraction(wanted, total))}. One of that colour and one from the total are then gone, so the second is ${toText(fraction(wanted - 1, total - 1))}. Multiplying gives ${toText(answer)}.`,
        hint: 'After the first draw there is one fewer altogether — and one fewer of that colour. Both numbers change.',
        steps: [
          { label: 'The first draw.', latex: toLatex(fraction(wanted, total)) },
          { label: 'The second, from what is left.', latex: toLatex(fraction(wanted - 1, total - 1)) },
          { label: 'Multiply along the branch.', latex: `${toLatex(fraction(wanted, total))} \\times ${toLatex(fraction(wanted - 1, total - 1))} = ${toLatex(answer)}` },
        ],
        traps: [
          // The first item put back, so the second fraction never changes.
          fractionTrap(replaced, 'replacement-assumed'),
          ...(added.n * answer.d === answer.n * added.d ? [] : [fractionTrap(added, 'compound-probability-added')]),
        ],
      });
    },
  }),

  template({
    id: 'compound-at-least-one',
    conceptId: 'compound-probability',
    description: 'Find "at least one" through the complement.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const total = rng.pick([3, 4, 5, 6, 8, 10]);
      const wanted = rng.int(1, total - 1);
      const missing = total - wanted;
      const answer = fraction(total * total - missing * missing, total * total);
      const both = fraction(wanted * wanted, total * total);
      const added = fraction(2 * wanted, total);

      return fractionExercise({
        prompt: `A spinner has ${total} equal sections, ${wanted} of which are gold. It is spun twice. What is the probability of landing on gold **at least once**? Give your answer as a fraction in its simplest form.`,
        value: answer,
        requireSimplified: true,
        summary: `The opposite of "at least once" is "never", with probability ${toText(fraction(missing, total))} × ${toText(fraction(missing, total))} = ${toText(fraction(missing * missing, total * total))}. Taking that from 1 leaves ${toText(answer)}.`,
        hint: 'There are three ways this can happen and only one way it cannot. Work out the one.',
        steps: [
          { label: 'The probability of no gold on one spin.', latex: toLatex(fraction(missing, total)) },
          { label: 'No gold on either spin.', latex: `${toLatex(fraction(missing, total))} \\times ${toLatex(fraction(missing, total))} = ${toLatex(fraction(missing * missing, total * total))}` },
          { label: 'Subtract from 1.', latex: `1 - ${toLatex(fraction(missing * missing, total * total))} = ${toLatex(answer)}` },
        ],
        traps: [
          // "Both times" answered instead: the two mixed outcomes never counted.
          ...(both.n * answer.d === answer.n * both.d ? [] : [fractionTrap(both, 'sample-space-undercounted')]),
          // The two spins added, which for a generous spinner exceeds 1.
          ...(added.n * answer.d === answer.n * added.d ? [] : [fractionTrap(added, 'compound-probability-added')]),
        ],
      });
    },
  }),

  /* ---------------------------------- experimental versus theoretical */
  template({
    id: 'experimental-from-trials',
    conceptId: 'experimental-probability',
    description: 'Find an experimental probability from a record of trials.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const trials = rng.pick([20, 25, 40, 50, 60, 80, 100, 120]);
      const sections = rng.int(3, 6);
      /*
       * The count has to be a plausible result for *this* spinner. Drawn
       * freely it produced 77 reds in 120 spins of a one-in-six spinner, which
       * teaches a student that the two probabilities have nothing to do with
       * each other — the opposite of the point. The wobble is a third of the
       * expected count either way, and never lands exactly on it.
       */
      const expected = Math.round(trials / sections);
      const wobble = Math.max(1, Math.round(expected / 3));
      const drift = rng.int(1, wobble) * (rng.bool() ? 1 : -1);
      const successes = Math.min(trials - 1, Math.max(1, expected + drift));
      const answer = fraction(successes, trials);
      const theoretical = fraction(1, sections);
      const odds = fraction(successes, trials - successes);
      const same = (a: typeof answer, b: typeof answer): boolean => a.n * b.d === b.n * a.d;

      return fractionExercise({
        prompt: `A spinner has ${sections} equal sections, one of them red. In ${trials} spins it landed on red ${successes} times. What is the **experimental** probability of red? Give your answer as a fraction in its simplest form.`,
        value: answer,
        requireSimplified: true,
        summary: `Experimental probability is what the trials gave: ${successes} reds out of ${trials} spins, which is ${toText(answer)}. The theoretical probability is ${toText(theoretical)} — a different number, worked out from the spinner rather than from the spins.`,
        hint: 'This question is about what happened, not what should happen. The number of trials goes underneath.',
        steps: [
          { label: 'Count the successes.', latex: `${successes}` },
          { label: 'Put the number of trials underneath.', latex: `\\frac{${successes}}{${trials}}` },
          { label: 'Simplify.', latex: toLatex(answer) },
        ],
        traps: [
          ...(same(theoretical, answer) ? [] : [fractionTrap(theoretical, 'theoretical-given-for-experimental')]),
          /*
           * Odds and the theoretical value collide more often than they look
           * like they should — 8 reds in 40 spins of a quarter-red spinner
           * gives 8/32 and 1/4, the same response with two diagnoses, and the
           * student is told about whichever error is listed first.
           */
          ...(same(odds, answer) || same(odds, theoretical) ? [] : [fractionTrap(odds, 'probability-as-odds')]),
          ...(successes === answer.n && answer.d === 1 ? [] : [trap(successes, 'probability-as-count')]),
        ],
      });
    },
  }),

  template({
    id: 'experimental-expected-count',
    conceptId: 'experimental-probability',
    description: 'Predict how many successes a theoretical probability leads you to expect.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const sections = rng.pick([2, 4, 5, 8, 10]);
      const wanted = rng.int(1, sections - 1);
      const multiple = rng.int(3, 15);
      const trials = sections * multiple;
      const answer = (trials * wanted) / sections;

      return numericExercise({
        prompt: `A bag holds ${sections} counters, ${wanted} of them blue. A counter is drawn and replaced ${trials} times. How many blue draws would you expect?`,
        unit: 'draws',
        value: answer,
        summary: `The theoretical probability of blue is ${wanted}/${sections}, so over ${trials} draws you expect ${wanted}/${sections} × ${trials} = ${answer}. The trials will not give exactly that, and the gap shrinks the longer you go on.`,
        hint: 'An expected count is the probability multiplied by the number of trials — not the number of counters in the bag.',
        steps: [
          { label: 'Find the theoretical probability.', latex: `\\frac{${wanted}}{${sections}}` },
          { label: 'Multiply by the number of trials.', latex: `\\frac{${wanted}}{${sections}} \\times ${trials} = ${answer}` },
        ],
        traps: [...(wanted === answer ? [] : [trap(wanted, 'probability-as-count')])],
      });
    },
  }),

  template({
    id: 'experimental-how-much-it-proves',
    conceptId: 'experimental-probability',
    description: 'Judge what a run of trials does and does not establish.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // A quarter of each of these is a whole number, and the overshoot stays
      // inside what chance alone produces — the answer is "nothing yet", so
      // the data must genuinely not show anything.
      const trials = rng.pick([20, 24, 28, 32, 40]);
      const expected = trials / 4;
      const seen = expected + rng.int(1, 3);

      const { choices, correctId } = options(
        [
          { label: 'Nothing yet — that gap is ordinary over so few trials', correct: true },
          { label: 'The spinner is biased towards red', misconceptionId: 'trials-taken-as-conclusive' },
          { label: 'Red is now less likely, to even the results out', misconceptionId: 'outcome-thought-due' },
          {
            label: `The probability of red has changed to ${seen}/${trials}`,
            misconceptionId: 'theoretical-given-for-experimental',
          },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `A spinner has four equal sections, one of them red. In ${trials} spins it landed on red ${seen} times, where ${expected} was expected. What does this show?`,
        choices,
        correctId,
        summary: `An experimental probability wanders around the theoretical one, and over only ${trials} spins a gap of ${seen - expected} is unremarkable. It would take a gap of that proportion over many hundreds of spins before the result said anything about the spinner.`,
        hint: 'Ask how many trials there were. Would you expect a short run to land exactly on the prediction?',
      });
    },
  }),

  template({
    id: 'experimental-next-trial',
    conceptId: 'experimental-probability',
    description: 'Say what a run of results does to the next trial.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const run = rng.int(4, 8);
      const item = rng.pick(['a fair coin', 'a fair coin', 'an unbiased coin'] as const);

      const { choices, correctId } = options(
        [
          { label: 'One half — exactly as it was on the first throw', correct: true },
          { label: 'Less than one half, because tails is due', misconceptionId: 'outcome-thought-due' },
          { label: 'More than one half, because the coin clearly favours heads', misconceptionId: 'trials-taken-as-conclusive' },
          {
            label: `One — it has landed heads all ${run} times so far`,
            misconceptionId: 'theoretical-given-for-experimental',
          },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `${item[0]!.toUpperCase()}${item.slice(1)} is thrown and lands heads ${run} times in a row. What is the probability that the next throw is heads?`,
        choices,
        correctId,
        summary: `The coin has no memory of the previous ${run} throws, so the next one is one half like every other. A run is never repaid by an opposite run — it is simply outweighed by the throws that follow.`,
        hint: 'Does the coin know what it did last time? What could carry that information from one throw to the next?',
      });
    },
  }),
];
